<template>
    <div>

        <main class="py-12 space-y-12">
            <BlogComponentRenderer
                v-if="!pending && postData?.sections?.length"
                :blocks="postData.sections"
            />

          <HighlightsSection/>
        </main>
    </div>
</template>

<script setup lang="ts">
import HexagonImage from '~/components/HexagonImage.vue';
import {BLOG_POST} from '~~/graphql/queries/findBlogPost';
import {BLOG_POST_PREVIEW} from '~~/graphql/queries/findBlogPostPreview';
import type {BlogPost} from '~~/types/strapi-blog';
import HighlightsSection from "~/components/HighlightsSection.vue";

type BlogPostResponse = {
    data: {
        blogPosts?: BlogPost[];
        blogPost?: BlogPost | null;
    };
};

const route = useRoute();
const graphql = useStrapiGraphQL();

const explicitPreview = computed<boolean | null>(() => {
    const value = String(route.query.preview || '').toLowerCase();

    if (value === 'true') return true;
    if (value === 'false') return false;

    return null;
});

const previewMode = usePreviewMode({
    shouldEnable: () => explicitPreview.value === true,
    onEnable: () => {
        refreshNuxtData();
    },
    onDisable: () => {
        refreshNuxtData();
    },
});

const {enabled, state} = previewMode;

const locale = computed(() => {
    const queryLocale = route.query.locale;
    return typeof queryLocale === 'string' && queryLocale.length
        ? queryLocale
        : 'de';
});

const slug = computed<string | null>(() => {
    const param = route.params.slug;

    if (Array.isArray(param)) {
        const joined = param.filter(Boolean).join('/');
        return joined.length ? joined : null;
    }

    if (typeof param === 'string' && param.length) {
        return param;
    }

    return null;
});

const documentId = computed<string | undefined>(() => {
    const value = route.query.documentId;
    return typeof value === 'string' && value.length ? value : undefined;
});

const previewToken = computed<string | undefined>(() => {
    const routeToken = route.query.token;

    if (typeof routeToken === 'string' && routeToken.length) {
        return routeToken;
    }

    return typeof state.token === 'string' && state.token.length
        ? state.token
        : undefined;
});

const requestedStatus = computed<'DRAFT' | 'PUBLISHED' | null>(() => {
    const value = String(route.query.status || '').toUpperCase();

    if (value === 'DRAFT' || value === 'PUBLISHED') {
        return value;
    }

    return null;
});

const status = computed<'DRAFT' | 'PUBLISHED'>(() => {
    if (requestedStatus.value) {
        return requestedStatus.value;
    }

    if (explicitPreview.value === false) {
        return 'PUBLISHED';
    }

    return enabled.value ? 'DRAFT' : 'PUBLISHED';
});

const isDraftPreview = computed(() => {
    return explicitPreview.value === true && status.value === 'DRAFT' && !!documentId.value;
});

watch(
    explicitPreview,
    async (value) => {
        if (value === false && enabled.value) {
            enabled.value = false;
            await refreshNuxtData();
        }

        if (value === true && !enabled.value) {
            enabled.value = true;
            await refreshNuxtData();
        }
    },
    {immediate: true},
);

if (!slug.value) {
    throw createError({
        statusCode: 400,
        statusMessage: 'Missing blog slug',
    });
}

const asyncDataKey = computed(() => {
    return [
        'blogPost',
        locale.value,
        slug.value,
        documentId.value ?? '',
        status.value,
        previewToken.value ?? '',
    ].join(':');
});

const {data: response, pending, error} = await useAsyncData<BlogPostResponse>(
    asyncDataKey,
    async () => {
        try {
            if (isDraftPreview.value && documentId.value) {
                return await $fetch<BlogPostResponse>('/api/blog-preview', {
                    method: 'POST',
                    body: {
                        documentId: documentId.value,
                        locale: locale.value,
                        status: status.value,
                      token: previewToken.value,
                    },
                });
            }

            return await graphql(BLOG_POST, {
                slug: slug.value,
                locale: locale.value,
                status: status.value,
            });
        } catch (err: any) {
            // Log full error server-side, but don't expose upstream details to the client
            console.error('Failed to load blog post:', err);
            throw createError({
                statusCode: 500,
                statusMessage: 'Failed to load blog post',
                data: {
                    message: 'Failed to load blog post',
                },
            });
        }
    },
    {
        watch: [slug, documentId, locale, status],
    },
);

const highlights = [
  {href: 'https://sauerland-spielgeraete.de/c/themenspiele/1', imgSrc: '1184944-1800_2.jpg', title: 'Themenspiele'},
  {href: 'https://sauerland-spielgeraete.de/c/serie-4.0-metall/86', imgSrc: 'BQ4C5242.jpg', title: 'Serie 4.0 Metall'},
  {href: 'https://sauerland-spielgeraete.de/c/klettern-und-motorik/20', imgSrc: 'BQ4C3666.jpg', title: 'Klettern & Motorik'},
  {href: 'https://sauerland-spielgeraete.de/c/sand-und-wasser/14', imgSrc: '1186668-1800_9.jpg', title: 'Sand & Wasser'},
  {href: 'https://sauerland-spielgeraete.de/c/multisportgeraete/28', imgSrc: 'BQ4C2940.jpg', title: 'Multisportgeräte'},
];

const postData = computed(() => {
    if (response.value?.data?.blogPost) {
        return response.value.data.blogPost;
    }

    return response.value?.data?.blogPosts?.[0] ?? null;
});

watch(
    error,
    (e) => {
        if (e) {
            showError(e);
        }
    },
    {immediate: true},
);

watchEffect(() => {
    if (!pending.value && !error.value && !postData.value) {
        throw createError({
            statusCode: 404,
            statusMessage:
                status.value === 'DRAFT'
                    ? 'Blog post not found for draft preview'
                    : 'Blog post not found',
        });
    }
});

const headerImage = computed(() => {
    const url = postData.value?.headerImage?.url;
    return url ? useStrapiMedia(url) : null;
});

const previewImage = computed(() => {
    const url = postData.value?.previewImage?.url;
    return url ? useStrapiMedia(url) : null;
});

const header = useState<{ title: string; subTitle: string }>('header');

header.value = {
    title: '',
    subTitle: '',
};
</script>

<style scoped>
</style>
